import { useState } from 'react';
import { useAuth } from '@Context/CognitoAuthProvider';
import { accountsAPI } from '@Lib/axios/account';
import { useMemo } from 'react';
import TextModalInput from '@Common/TextModalInput';
import showToast from '@Common/toast/showToast';
import { EditPromptModalProps } from '@Models/types';

const EditPromptModal = ({
  isOpen,
  onClose,
  selectedPrompt,
  onSuccess,
  getNextVersion,
}: EditPromptModalProps) => {
  const { user, logout } = useAuth();
  const account = useMemo(() => accountsAPI(logout), [logout]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePlaceholders = (content: string) => {
    const requiredPlaceholders = [
      '{user_story}',
      '{test_collection}',
      '{context}',
    ];
    const missingPlaceholders = requiredPlaceholders.filter(
      (placeholder) => !content.includes(placeholder)
    );
    return missingPlaceholders;
  };

  const handleEdit = async (content: string, nickname?: string) => {
    if (!selectedPrompt) return;

    const missingPlaceholders = validatePlaceholders(content);
    if (missingPlaceholders.length > 0) {
      showToast.error(
        `Missing required placeholders: ${missingPlaceholders.join(', ')}`
      );
      return;
    }
    setIsSubmitting(true);
    try {
      await account.addPrompt(
        user?.profile?.email || '',
        content,
        getNextVersion(),
        {
          name: user?.profile?.name || '',
          email: user?.profile?.email || '',
        },
        true,
        nickname || undefined
      );

      onSuccess?.();
      onClose();
      showToast.success('Prompt updated successfully!');
    } catch (error) {
      console.error('Error updating prompt:', error);
      showToast.error('Failed to update prompt');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TextModalInput
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleEdit}
      title={
        selectedPrompt
          ? `Edit Prompt - Version ${selectedPrompt.version}`
          : 'Edit Prompt'
      }
      value={selectedPrompt?.content || ''}
      nickname={selectedPrompt?.nickname || ''}
      showNickname={true}
      loading={isSubmitting}
      submitBtnText="Save Changes"
      cancelBtnText="Cancel"
      information={{
        title: 'Important Note:',
        description:
          'The prompt you enter will be appended to a pre-determined user story and format to ensure consistency in the generated test cases. Please make sure to include {user_story}, {test_collection}, and {context} placeholders.',
      }}
    />
  );
};

export default EditPromptModal;
